

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiTrash2, FiUser, FiMapPin } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserById, updateUser, deleteUser } from "../../redux/slices/userSlice";
import { FaClinicMedical, FaStethoscope, FaRegEnvelope, FaIdCard } from "react-icons/fa";
import { MdPhoneInTalk, MdOutlinePets } from "react-icons/md";
import { IoMdTime } from "react-icons/io";
import DeleteModal from "../../components/common/DeleteModal";
import { toast } from "react-toastify";

export default function UserProfileEdit() {
  const { type, id } = useParams();
  const userType = type;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const profile = useSelector((state) => state.users.profile);
  const profileStatus = useSelector((state) => state.users.profileStatus);

  const [formData, setFormData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const isPetParent = type === "pet-parents";

  useEffect(() => {
    dispatch(fetchUserById({ userType, id }));
  }, [dispatch, userType, id]);

  useEffect(() => {
    if (profile) {
      setFormData({
        ...profile,
        first_name: profile.first_name || "",
        middle_name: profile.middle_name || "",
        last_name: profile.last_name || "",
        // Ensure pets data is properly included with IDs
        pets: profile.pets || [],
        // Ensure nested vendor data is properly set with all required fields
        vendor: {
          clinic_name: profile.vendor?.clinic_name || "",
          specialization: profile.vendor?.specialization || "",
          years_of_experience: profile.vendor?.years_of_experience || "",
          license_no: profile.vendor?.license_no || "",
          kyc_status: profile.vendor?.kyc_status || "",
          ...profile.vendor // Spread any other existing vendor fields
        }
      });
    }
  }, [profile]);

  if (profileStatus === "loading") return <div className="px-20 pt-10">Loading...</div>;
  if (profileStatus === "failed") return <div className="px-20 pt-10">Failed to load user data.</div>;
  if (!profile) return <div className="px-20 pt-10">User not found.</div>;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVendorChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      vendor: {
        ...prev.vendor,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    console.log('UserProfileEdit: Saving user data:', {
      id,
      userType,
      formData
    });

    // Create FormData for backend (as expected by Postman example)
    const formDataToSend = new FormData();

    // Add basic user fields - separate name fields
    if (formData.first_name) formDataToSend.append('first_name', formData.first_name);
    if (formData.middle_name) formDataToSend.append('middle_name', formData.middle_name);
    if (formData.last_name) formDataToSend.append('last_name', formData.last_name);

    // Only add mobile if it has changed to avoid validation conflicts
    if (profile && formData.mobile !== profile.mobile) {
      formDataToSend.append('mobile', formData.mobile);
    } else {
      console.log('UserProfileEdit: Mobile number unchanged, not including in update payload');
    }

    if (formData.country_code) formDataToSend.append('country_code', formData.country_code);
    if (formData.email) formDataToSend.append('email', formData.email);

    // Add vendor details in bracket notation as expected by backend
    if (!isPetParent) {
      // Always include vendor type for veterinarians
      formDataToSend.append('vendor_details[type]', 'veterinarian');

      if (formData.vendor?.clinic_name) formDataToSend.append('vendor_details[clinic_name]', formData.vendor.clinic_name);
      if (formData.vendor?.years_of_experience) formDataToSend.append('vendor_details[years_of_experience]', formData.vendor.years_of_experience);
      if (formData.vendor?.office_phone) formDataToSend.append('vendor_details[office_phone]', formData.vendor.office_phone);
      if (formData.vendor?.gstin) formDataToSend.append('vendor_details[gstin]', formData.vendor.gstin);
      if (formData.vendor?.biography) formDataToSend.append('vendor_details[biography]', formData.vendor.biography);
      if (formData.vendor?.license_no) formDataToSend.append('vendor_details[license_no]', formData.vendor.license_no);
      if (formData.vendor?.specialization) formDataToSend.append('vendor_details[specialization]', formData.vendor.specialization);
    }

    // Add pets data in the array format as expected by backend
    if (isPetParent && formData.pets && formData.pets.length > 0) {
      formData.pets.forEach((pet) => {
        if (pet.uuid) formDataToSend.append(`pets[][id]`, pet.uuid);
        if (pet.name) formDataToSend.append(`pets[][name]`, pet.name);
        if (pet.breed) formDataToSend.append(`pets[][breed]`, pet.breed);
        if (pet.date_of_birth) formDataToSend.append(`pets[][date_of_birth]`, pet.date_of_birth);
        if (pet.species) formDataToSend.append(`pets[][species]`, pet.species);
        if (pet.sex) formDataToSend.append(`pets[][sex]`, pet.sex);
      });
    }

    console.log('UserProfileEdit: FormData to send to backend:');
    for (let [key, value] of formDataToSend.entries()) {
      console.log(`${key}: ${value}`);
    }

    try {
      await dispatch(updateUser({ id, userType, data: formDataToSend })).unwrap();
      toast.success('User updated successfully!');
      navigate(-1);
    } catch (error) {
      const errorMessage = error?.response?.data?.meta?.message || error?.message || 'Failed to update user. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleDelete = () => {
    dispatch(deleteUser({ userType, id }));
    setModalOpen(false);
    navigate(-2);
  };

  const handlePetChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedPets = [...(prev.pets || [])];
      updatedPets[index] = { ...updatedPets[index], [field]: value };
      return { ...prev, pets: updatedPets };
    });
  };

  return (
    <div className="relative min-h-screen bg-white font-inter px-6 pt-12">
      {/* Top bar */}
      <div className="flex justify-between items-start mb-10">
        <div className="flex items-start">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="mt-1 mr-3 p-2 text-2xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-xl font-bold text-black">
              Edit {isPetParent ? "Pet Parent" : "Dr."} {[formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(' ')}
            </div>
            <div className="text-gray-600 text-sm mt-1">Update information and settings</div>
          </div>
        </div>

        <div className="flex gap-3">
          {/* Delete Button */}
          <button
            onClick={() => setModalOpen(true)}
            className="p-2 text-gray-600 hover:text-red-500"
          >
            <FiTrash2 size={20} />
          </button>

          <button
            onClick={() => navigate(-1)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#347DFF] text-white rounded-lg px-5 py-2 text-base font-medium hover:bg-blue-600 transition"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Form Sections */}
      <div className="space-y-10 px-10">
        {/* Basic Information */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">
              <FiUser size={22} />
            </span>{" "}
            Basic Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                name="first_name"
                value={formData.first_name || ""}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Middle Name</label>
              <input
                name="middle_name"
                value={formData.middle_name || ""}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter middle name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                name="last_name"
                value={formData.last_name || ""}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter last name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email Address</label>
              <input
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone Number</label>
              <input
                name="mobile"
                value={formData.mobile || ""}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter phone number"
              />
            </div>
         
          </div>
        </div>

        {/* Professional Details - Only for Veterinarians */}
        {!isPetParent && (
          <div>
            <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
              <span className="rounded p-1">
                <FaStethoscope size={22} />
              </span>{" "}
              Professional Details
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Clinic Name</label>
                <input
                  name="clinic_name"
                  value={formData.vendor?.clinic_name || ""}
                  onChange={(e) => handleVendorChange('clinic_name', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter clinic name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Specialization</label>
                <input
                  name="specialization"
                  value={formData.vendor?.specialization || ""}
                  onChange={(e) => handleVendorChange('specialization', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter specialization"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Years of Experience</label>
                <input
                  name="years_of_experience"
                  type="number"
                  value={formData.vendor?.years_of_experience || ""}
                  onChange={(e) => handleVendorChange('years_of_experience', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter years of experience"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">License Number</label>
                <input
                  name="license_no"
                  value={formData.vendor?.license_no || ""}
                  onChange={(e) => handleVendorChange('license_no', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter license number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GSTIN</label>
                <input
                  name="gstin"
                  value={formData.vendor?.gstin || ""}
                  onChange={(e) => handleVendorChange('gstin', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter GSTIN"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Biography</label>
                <textarea
                  name="biography"
                  value={formData.vendor?.biography || ""}
                  onChange={(e) => handleVendorChange('biography', e.target.value)}
                  rows="3"
                  className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter biography"
                />
              </div>
            </div>
          </div>
        )}

        {/* Pet Parent Section */}
        {isPetParent && (
          <div>
            <div className="flex items-center gap-2 text-gray-700 font-medium mb-5">
              <span className="rounded p-1">🐾</span> Pet Details
            </div>
            <div className="space-y-4">
              {(formData.pets || []).map((pet, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-1">Pet Name</label>
                    <input
                      value={pet.name || ""}
                      onChange={(e) => handlePetChange(idx, "name", e.target.value)}
                      placeholder="Pet Name"
                      className="w-full rounded-lg px-3 py-2 bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Breed</label>
                    <input
                      value={pet.breed || ""}
                      onChange={(e) => handlePetChange(idx, "breed", e.target.value)}
                      placeholder="Breed"
                      className="w-full rounded-lg px-3 py-2 bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={pet.date_of_birth || ""}
                      onChange={(e) => handlePetChange(idx, "date_of_birth", e.target.value)}
                      className="w-full rounded-lg px-3 py-2 bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ))}
              {(formData.pets || []).length === 0 && (
                <div className="text-center text-gray-500 py-4">
                  No pets found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDelete={handleDelete}
        profileType={isPetParent ? "Pet Parent Profile" : "Veterinarian Profile"}
      />
    </div>
  );
}
